import * as React from 'react';
import './Home.css'
import { Box, Button, CircularProgress, Divider, Link, TextField, Typography } from '@mui/material';
import { getApiHost } from '../@shared/util';
import { useSelector } from 'react-redux';
import { getAppConfig, setIsAuthorized, setUserIsLoading } from '../reducers/main/mainReducer';
import { FlexBoxColumn, InputBaseSx, LinkSx, OAuthBtnSx, PageHeaderSx, PrimaryBtnSx } from '../@shared/templates';
import { Palette } from '../@shared/palette';
import { UserProviderInstance } from '../@shared/services/UserProvider';
import { useDispatch } from 'react-redux';
import { onThrowAlert } from '../sagas/dataSaga/dataSagaActions';
import { NotificationModel } from '../reducers/main/mainModel';
import { useNavigate } from 'react-router-dom';

const Login = () => {

  const appConfig = useSelector(getAppConfig);


  return <Box sx={{width: "100%", height: "100%", background: "url(/loginBackground.jpeg) no-repeat center center", backgroundSize: "cover"}}>
    <Box
      sx={{
        marginTop: 0,
        marginBottom: 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: "60%",
        height: "100%",
        background: Palette.backgroundColorCard,
      }}
    >
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-evenly",
        width: "100%"
      }}>
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-evenly",
          width: "500px"
        }}>
          <Typography sx={{ ...PageHeaderSx, marginBottom: "20px" }}>
            Sign in to MLArtisan
          </Typography>
          {appConfig.isSelfHostedServiceLoginEnabled && <StandaloneLogin />}
          <Divider orientation="horizontal" sx={{marginBottom: "30px", width: "100%"}} />
          {appConfig.isGoogleEnabled && <GoogleLogin />}
          <Box sx={{marginTop: "20px"}}>
            <Typography sx={{ color: Palette.textColorSecondary, fontSize: "0.9rem" }}>
              By signing in, you agree to our&nbsp;
              <Link sx={LinkSx} href="https://mlartisan.com/terms" target="_blank">Terms of Service</Link>
              &nbsp;and&nbsp;
              <Link sx={LinkSx} href="https://mlartisan.com/privacy" target="_blank">Privacy Policy</Link>
            </Typography>
          </Box>
        </Box>
        
      </Box>
      
    </Box>
  </Box>
  
}

const StandaloneLogin = () => {
  const [username, setUsername] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const userProviderInstance = React.useMemo(() => UserProviderInstance, []);
  const onAuth = React.useCallback(async () => {
    setIsLoading(true);
    var res = await userProviderInstance.standaloneLogin(username, password);
    if (res.status !== 200) {
      dispatch(onThrowAlert({
        message: res.data,
        lifeTime: 10000
      } as NotificationModel));
    } else {
      const token = res.data;
      localStorage.setItem('token', token);

      navigate('/');
      dispatch(setUserIsLoading(true));

      try {
        const currentUser = await UserProviderInstance.current()
        if (currentUser.status === 200) {
          dispatch(setIsAuthorized(currentUser.data));
        }
        else {
          dispatch(setIsAuthorized(undefined));
          navigate('/login');
        }
      }
      catch (error) {
        dispatch(onThrowAlert({
          message: (error as Error).message,
          lifeTime: 10000
        } as NotificationModel));
      }
      finally {
        dispatch(setUserIsLoading(false));
      }
    }

    setIsLoading(false);
  }, [userProviderInstance, username, password, dispatch, navigate]);

  return <Box sx={{ ...FlexBoxColumn, width: "100%", marginBottom:"30px", alignItems: "stretch" }}>
    <TextField sx={{...InputBaseSx}} size="small" value={username} onChange={(e) => setUsername(e.target.value)} fullWidth label="Username" variant="outlined" />
    <TextField sx={{...InputBaseSx, marginBottom: "20px"}} type='password' size="small" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth label="Password" variant="outlined" />
    <Button disabled={isLoading} sx={PrimaryBtnSx} onClick={onAuth}>Login{isLoading && (
      <CircularProgress
        size={24}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          marginTop: '-12px',
          marginLeft: '-12px',
          color: Palette.btnPrimary
        }}
      />
    )}</Button>
  </Box>
}

const GoogleLogin = () => {
  return <Box sx={{ width: "100%" }}>
    <a href={`${getApiHost()}/auth/login/google`} style={{ textDecoration: 'none' }}>
      <Button
        type="button"
        fullWidth
        sx={{
          ...OAuthBtnSx,
          fontSize: "1rem",
          display: "flex",
          justifyContent: "space-between",
          padding: "10px 12px",
          textDecoration: "none",
        }}
        endIcon={<Box/>}
        startIcon={<svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width={20}><g><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path><path fill="none" d="M0 0h48v48H0z"></path></g></svg>}
      >
        Continue with Google
      </Button>
    </a>
  </Box>
}

export default Login;
